import { Link } from "react-router-dom";
import { useSeo } from "../lib/useSeo";
import { Mail, FileText } from "lucide-react";

const SITE_URL = "https://railwaycareers.co.uk";
const CONTACT_EMAIL = "datacontroller@railwaycareers.co.uk";
const LAST_UPDATED = "29 April 2026";

const SECTIONS = [
  {
    id: "intro",
    h: "1. Introduction",
    body: [
      "These Terms of Service (\"Terms\") govern your use of the Railway Careers UK website at railwaycareers.co.uk (\"the Service\"), operated by Railway Careers UK (\"we\", \"us\", \"our\").",
      "By accessing or using the Service you agree to be bound by these Terms. If you do not agree, please do not use the Service.",
      "These Terms are governed by the laws of England and Wales. Any dispute will be subject to the exclusive jurisdiction of the courts of England and Wales.",
    ],
  },
  {
    id: "service",
    h: "2. The Service",
    body: [
      "Railway Careers UK is an independent UK rail recruitment platform. The Service includes a searchable database of UK rail vacancies, role guides, a postcode-based Vacancy Map, an Operators directory, the weekly Monday Briefing newsletter, and an employer self-serve portal for posting vacancies.",
      "We aggregate publicly available UK rail vacancies via licensed feeds (currently Adzuna) and accept direct postings from verified rail employers. Where possible, application links go directly to the employer's careers page; we do not act as a recruitment agency and do not collect placement fees from employers or job seekers.",
    ],
  },
  {
    id: "eligibility",
    h: "3. Eligibility",
    list: [
      "You must be at least 16 years old to use the Service.",
      "Employer accounts are intended for legitimate UK rail employers, recruiters acting on behalf of named UK rail employers, training providers and rail-industry organisations.",
      "You must provide accurate, complete and up-to-date information when registering.",
      "We reserve the right to refuse, suspend or terminate any account at our discretion, including where there is evidence of misuse, misrepresentation or breach of these Terms.",
    ],
  },
  {
    id: "accounts",
    h: "4. Accounts and security",
    list: [
      "You are responsible for maintaining the confidentiality of your password and for all activity under your account.",
      "Notify us immediately at the contact below if you suspect unauthorised access.",
      "Employer accounts require admin approval before vacancies can be posted.",
      "We may require email verification, phone verification or company verification to maintain an account in good standing.",
      "You must not share, sell or transfer your account credentials.",
    ],
  },
  {
    id: "acceptable-use",
    h: "5. Acceptable use",
    body: ["You agree not to:"],
    list: [
      "Use the Service for anything unlawful, fraudulent or misleading.",
      "Post discriminatory, defamatory, obscene, harassing or otherwise unlawful content.",
      "Post job listings that breach the Equality Act 2010, the Employment Rights Act 1996 or any other UK employment law.",
      "Post fake or speculative vacancies, MLM schemes, training-pay-to-apply schemes, or vacancies outside the UK rail industry.",
      "Scrape, harvest, copy or republish content from the Service without our prior written permission.",
      "Reverse-engineer, decompile or attempt to extract source code from the Service.",
      "Probe, scan, attack, test the vulnerability of, or interfere with the operation of the Service or any user's experience of it.",
      "Use automated tools, bots or AI agents to submit applications, registrations or vacancies without our written permission.",
      "Impersonate another person or organisation.",
      "Use the Service to send unsolicited commercial communications (spam).",
    ],
  },
  {
    id: "vacancy-content",
    h: "6. Vacancy content (employers)",
    list: [
      "Employers retain ownership of the vacancy content they post.",
      "By posting a vacancy you grant Railway Careers UK a non-exclusive, royalty-free, worldwide licence to display, distribute, summarise, syndicate (e.g. RSS, sitemap, Google for Jobs), and include the vacancy in newsletters and marketing communications about the Service, for the duration of the vacancy and a reasonable archive period thereafter.",
      "You warrant that any vacancy you post is accurate, lawful, non-discriminatory, and that you have authority to publish it.",
      "We may edit, refuse, remove or relocate any vacancy at our discretion, including for clarity, quality, formatting, geographic relevance or legal compliance.",
      "Featured-listing fees, where charged, are non-refundable once the listing is published.",
      "Expired or filled vacancies are removed automatically. Employers may extend or close vacancies via the dashboard.",
    ],
  },
  {
    id: "applicant-content",
    h: "7. Applicant data and applications",
    list: [
      "We do not host CVs, application forms or applicant profiles. Applications are made directly to the employer via the link provided on each vacancy.",
      "Job seekers must comply with each employer's individual application process and Terms.",
      "We are not responsible for an employer's response, decision, communications or recruitment outcome.",
      "Newsletter subscribers may receive the Monday Briefing and (if opted in) operator-specific job alerts. Unsubscribe links are included in every email.",
    ],
  },
  {
    id: "ip",
    h: "8. Intellectual property",
    body: [
      "All Railway Careers UK branding, design, code, role guides, FAQs, written content, and the curated arrangement of operator and vacancy data are the intellectual property of Railway Careers UK and are protected under UK and international copyright and database-right law.",
      "You may not copy, reproduce, republish, scrape, syndicate or build a competing product using our content without prior written permission. The licensed RSS feed at /api/feed.xml is provided for personal or aggregator use only, not for republishing.",
    ],
  },
  {
    id: "third-party-links",
    h: "9. Third-party links and services",
    body: [
      "The Service contains links to third-party websites — including operator careers pages, partner sites, and aggregated job sources. We do not control these third parties and are not responsible for their content, terms, accuracy, availability or privacy practices. Following any link is at your own risk.",
      "Map data is provided by OpenStreetMap contributors under the Open Database Licence. Driving times are estimates from OSRM or a haversine fallback and should not be relied on for journey planning.",
    ],
  },
  {
    id: "fees",
    h: "10. Fees",
    body: [
      "The Service is free to use for job seekers. Employers may post a standard vacancy free of charge after admin approval. Featured-listing placements are subject to a flat fee disclosed on the For Employers page. All fees are quoted in GBP and subject to UK VAT where applicable.",
      "Refunds are not offered for featured listings once published, except where we cancel the listing for our own reasons.",
    ],
  },
  {
    id: "warranties",
    h: "11. No warranties",
    body: [
      "The Service is provided on an \"as-is\" and \"as-available\" basis. We make no representation or warranty, express or implied, that the Service will be uninterrupted, error-free, secure, accurate, complete, current, or that any vacancy will be live, available or successfully filled.",
      "Salary ranges, training durations, eligibility criteria and recruitment timelines on role guides are indicative only — actual figures vary by employer, location, grade, route and vacancy. Always check the individual vacancy and operator guidance before applying.",
      "Nothing in these Terms excludes or limits any liability that cannot be excluded under English law, including liability for death or personal injury caused by negligence, or for fraud.",
    ],
  },
  {
    id: "liability",
    h: "12. Limitation of liability",
    body: [
      "To the fullest extent permitted by English law, Railway Careers UK shall not be liable for any indirect, special, consequential, incidental, exemplary or punitive damages, including (without limitation) loss of profits, loss of opportunity, loss of earnings, loss of data, or reputational harm, arising out of or in connection with your use of the Service or any vacancy listed on it.",
      "Our total aggregate liability to any user (employer or job seeker) in any 12-month period shall not exceed the greater of (a) £100, or (b) the total fees paid by that user to Railway Careers UK in the same 12-month period.",
    ],
  },
  {
    id: "termination",
    h: "13. Termination",
    list: [
      "You may close your account at any time by emailing the contact below.",
      "We may suspend or terminate your account, with or without notice, for breach of these Terms, suspected fraud, abuse, security risk, or for any other lawful reason.",
      "Sections relating to intellectual property, no warranties, limitation of liability and governing law survive termination.",
    ],
  },
  {
    id: "changes",
    h: "14. Changes to these Terms",
    body: [
      "We may update these Terms from time to time. The \"Last updated\" date at the top of this page reflects the most recent revision. Continued use of the Service after changes constitutes acceptance of the updated Terms. Material changes will be communicated via email to registered users where appropriate.",
    ],
  },
  {
    id: "law",
    h: "15. Governing law and jurisdiction",
    body: [
      "These Terms and any dispute or claim arising out of or in connection with them or their subject matter (including non-contractual disputes) are governed by and construed in accordance with the laws of England and Wales. The courts of England and Wales have exclusive jurisdiction.",
    ],
  },
  {
    id: "contact",
    h: "16. Contact us",
    body: [
      `Questions about these Terms? Contact us at ${CONTACT_EMAIL}.`,
      "For data protection and privacy questions, see our Privacy Policy.",
    ],
  },
];

function Bullet({ html }) {
  return (
    <li className="flex gap-3 text-[15px]">
      <span className="text-amber-700 mt-1.5 shrink-0">•</span>
      <span className="text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: html.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-ink">$1</strong>') }}
      />
    </li>
  );
}

export default function Terms() {
  useSeo({
    title: "Terms of Service | Railway Careers UK",
    description:
      "Railway Careers UK Terms of Service — acceptable use, employer vacancy posting terms, intellectual property, fees, liability and governing law for the UK rail recruitment platform.",
    canonical: "/terms",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Terms of Service — Railway Careers UK",
      url: `${SITE_URL}/terms`,
      lastReviewed: "2026-04-29",
    },
  });

  return (
    <div className="bg-cream min-h-screen" data-testid="terms-page">
      <section className="bg-navy text-bone">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber font-semibold">
            <FileText className="w-3.5 h-3.5" /> Legal
          </div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight leading-[1.1] mt-3">
            Terms of Service
          </h1>
          <p className="text-bone/70 mt-4 text-sm">
            Last updated: <span className="font-mono text-bone">{LAST_UPDATED}</span>
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="mt-6 inline-flex items-center gap-2 text-sm bg-bone/5 border border-amber/30 hover:border-amber rounded-md px-3 py-2 transition-colors"
            data-testid="terms-contact-email"
          >
            <Mail className="w-4 h-4 text-amber" />
            <span className="font-mono">{CONTACT_EMAIL}</span>
          </a>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16 grid md:grid-cols-[200px_1fr] gap-10">
        {/* Sticky TOC (desktop) */}
        <aside className="md:sticky md:top-24 md:self-start hidden md:block">
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink font-bold mb-4">Contents</div>
          <ul className="space-y-2 text-[13px]">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-muted-foreground hover:text-ink transition-colors">
                  {s.h}
                </a>
              </li>
            ))}
          </ul>
        </aside>

        <article className="space-y-12 prose-slate max-w-none">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-32">
              <h2 className="font-display font-bold text-ink text-2xl tracking-tight">{s.h}</h2>
              {s.body?.map((p, i) => (
                <p key={i} className="text-muted-foreground leading-relaxed mt-3 text-[15px]">{p}</p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-2">
                  {s.list.map((item, i) => <Bullet key={i} html={item} />)}
                </ul>
              )}
            </section>
          ))}

          <div className="border-t border-border pt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <Link to="/privacy" className="text-muted-foreground hover:text-ink underline">Privacy Policy</Link>
            <Link to="/" className="text-muted-foreground hover:text-ink underline">← Back to Railway Careers UK</Link>
          </div>
        </article>
      </main>
    </div>
  );
}
